
import { useState, useEffect, useRef } from 'react';
import { Loader2, FileText, FileType } from 'lucide-react';
import { Document, Packer, Paragraph, HeadingLevel, AlignmentType, TextRun } from "docx";
import { saveAs } from "file-saver";
import { marked } from "marked";
import { toast } from "sonner";

// Actually, I didn't install dompurify. I'll just skip it for now or rely on marked's default sanitation if enabled (it's deprecated).
// I'll trust the LLM output for now as it's internal tool usage.

import apiService from '../../../services/api';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { ACTIONS } from '../../../telemetry/constants/actions';
import { subscriptionService } from '../../../services/subscriptionService';
import { creditService } from '../../../services/creditService';
import { CREDIT_COSTS, CREDIT_THRESHOLDS } from '../../../config/creditCosts';
import LowCreditModal from '../../../components/LowCreditModal';
import CreditDeductionConfirmModal from '../../../components/CreditDeductionConfirmModal';
import BlogRichEditor, { htmlToMarkdown } from './BlogRichEditor';
import { useQueryClient } from '@tanstack/react-query';

const getFullImageUrl = (url?: string) => {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001/api/v1').replace(/\/api\/v1\/?$/, '');
  if (url.startsWith('/generated_images')) {
    return `${baseUrl}/ai_engine${url}`;
  }
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};
import { useCredits } from '../../../hooks/useCredits';
interface BlogProps {
  onTooltipShow: (title: string, content: string) => void;
  onBack: () => void;
  darkMode?: boolean;
  userId: string;
}

interface BlogTopic {
  id: number;
  title: string;
  searchVolume: number;
  competition: string;
  difficulty: number;
  selected: boolean;
}

interface Author {
  id: number;
  name: string;
  profile_picture_url?: string;
  role?: string;
  writing_style?: string; // Mapped from backend 'writing_style'
}

interface GeneratedBlog {
  id: number;
  topic: string;
  author: string;
  wordCount: number;
  readTime: number; // calculated or mocked
  published: boolean;
  content: string;
  image_url?: string;
  created_at?: string;
}

interface Quota {
  used: number;
  limit: number;
  remaining: number;
  reset_date?: string;
}

export default function Blog({ onTooltipShow, onBack, darkMode, userId }: BlogProps) {
  const { invalidateCredits } = useCredits();
  const [activeTab, setActiveTab] = useState<'discovery' | 'generated'>(() => {
    const stored = localStorage.getItem('blog_active_tab');
    if (stored === 'generated') {
      localStorage.removeItem('blog_active_tab');
      return 'generated';
    }
    if (typeof window !== 'undefined' && window.location.hash.includes('generated')) {
      return 'generated';
    }
    return 'discovery';
  });
  
  // Low Credit Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCredits, setModalCredits] = useState(0);
  const [modalRequired, setModalRequired] = useState(0);
  const [modalIsBlocker, setModalIsBlocker] = useState(false);

  const checkCreditsForAction = async (required: number): Promise<{ allowed: boolean; current: number }> => {
    try {
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;
      
      if (current < required) {
        setModalCredits(current);
        setModalRequired(required);
        setModalIsBlocker(true);
        setModalOpen(true);
        return { allowed: false, current };
      }
      return { allowed: true, current };
    } catch (err) {
      console.error("Failed to check credits:", err);
    }
    return { allowed: true, current: 0 };
  };

  const checkLowCreditWarning = async (current: number, quotaRemaining?: number) => {
    const remaining = quotaRemaining !== undefined ? quotaRemaining : (quota?.remaining ?? 0);
    if (remaining > 0) {
      return;
    }
    if (current < CREDIT_THRESHOLDS.LOW_CREDIT_WARNING) {
      setModalCredits(current);
      setModalIsBlocker(false);
      setModalOpen(true);
    }
  };
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `Blog Generator / ${activeTab === 'discovery' ? 'Discovery' : 'Generated Blogs'}`,
    routePath: `/dashboard#blog/${activeTab}`,
  });
  const { trackAction } = useTelemetry({ module: 'SEO Manager', submodule: 'Blog Generator' });

  // Data State
  const [topics, setTopics] = useState<BlogTopic[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [generatedBlogs, setGeneratedBlogs] = useState<GeneratedBlog[]>([]);

  // UI State
  const queryClient = useQueryClient();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showStartNoticeModal, setShowStartNoticeModal] = useState(false);
  const [completedTopicIds, setCompletedTopicIds] = useState<Record<number, boolean>>({});
  const [failedTopicErrors, setFailedTopicErrors] = useState<Record<number, string>>({});
  const [waitingForQuota, setWaitingForQuota] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<{ current: number, total: number }>({ current: 0, total: 0 });
  const [activeJobId, setActiveJobId] = useState<string | null>(() => localStorage.getItem('blog_generation_active_job_id') || null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStep, setJobStep] = useState<string>('Preparing...');
  const [quota, setQuota] = useState<Quota | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [downloadingPdfBlogId, setDownloadingPdfBlogId] = useState<number | null>(null);
  const [downloadingDocxBlogId, setDownloadingDocxBlogId] = useState<number | null>(null);
  const pollIntervalRef = useRef<any>(null);
  const [generationQueue, setGenerationQueue] = useState<any[]>([]);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);

  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [creditConfirmAmount, setCreditConfirmAmount] = useState(0);
  const [creditConfirmBalance, setCreditConfirmBalance] = useState(0);
  const [creditConfirmDescription, setCreditConfirmDescription] = useState('');
  const [isConfirmingGeneration, setIsConfirmingGeneration] = useState(false);
  const [isSubmittingGeneration, setIsSubmittingGeneration] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [publishingBlogId, setPublishingBlogId] = useState<number | null>(null);
  // Track retry attempts per blog image. Pollinations generates images on first
  // request which can take 30-60s; if the browser times out, we retry.
  // A ref tracks the count (safe from Strict Mode double-invocations); state
  // holds the same value just to trigger a re-render with a new img key.

  // imgFallbacks: tracks blog IDs whose src load failed (no fallback URL — just hides the img)
  const [imgFallbacks, setImgFallbacks] = useState<Record<number, boolean>>({});

  const handleImageError = (blogId: number) => {
    if (imgFallbacks[blogId]) return; // already marked
    setImgFallbacks(prev => ({ ...prev, [blogId]: true }));
  };

  // Per-blog image regeneration loading state
  const [regeneratingImageId, setRegeneratingImageId] = useState<number | null>(null);

  const handleRegenerateImage = async (blog: GeneratedBlog) => {
    setRegeneratingImageId(blog.id);
    try {
      const res = await apiService.regenerateBlogImage(blog.id, userId);
      const newUrl = res?.data?.image_url;
      if (newUrl) {
        setGeneratedBlogs(prev =>
          prev.map(b => b.id === blog.id ? { ...b, image_url: newUrl } : b)
        );
        // Clear any error flag so the new image renders
        setImgFallbacks(prev => { const n = { ...prev }; delete n[blog.id]; return n; });
      } else {
        alert('Poster regeneration failed. Please try again.');
      }
    } catch (e) {
      console.error('Failed to regenerate blog poster:', e);
      alert('Poster regeneration failed. Please try again.');
    } finally {
      setRegeneratingImageId(null);
    }
  };

  const handlePublishToggle = async (blog: GeneratedBlog) => {
    trackAction(blog.published ? 'edit' : 'publish', blog.published ? 'Revert blog to draft' : 'Publish blog', {
      blog_id: blog.id,
      topic: blog.topic,
    });
    setPublishingBlogId(blog.id);
    try {
      const data = await apiService.publishBlog(blog.id, userId);
      const newPublished = data.data?.published ?? !blog.published;
      setGeneratedBlogs(prev =>
        prev.map(b => b.id === blog.id ? { ...b, published: newPublished } : b)
      );
    } catch (e) {
      console.error('Failed to update publish status', e);
    } finally {
      setPublishingBlogId(null);
    }
  };

  // 3. Generate Blogs
  // Entry point: figures out how many of the selected topics are covered by
  // the free monthly quota vs. will be credit-billed, and — for the paid
  // portion only — checks balance sufficiency then asks for confirmation
  // before actually generating. Topics within quota still generate
  // immediately with no popup, same as before.
  const handleGenerateBlogs = async () => {
    if (isGenerating || isSubmittingGeneration) return;
    const selectedTopics = topics.filter(t => t.selected);
    if (selectedTopics.length === 0) return;

    setIsSubmittingGeneration(true);
    try {
      // Re-fetch fresh quota to ensure accurate free/paid split calculation
      let latestQuota = quota;
      try {
        const data = await apiService.getBlogQuota(userId);
        const q = data.data?.quota || data.data;
        if (q) {
          setQuota(q);
          latestQuota = q;
        }
      } catch (e) {
        console.error("Failed to refresh quota before generation check", e);
      }

      const freeRemaining = Math.max(0, latestQuota?.remaining ?? 0);
      let availableFree = freeRemaining;
      let paidCount = 0;
      let freeCount = 0;

      for (const _topic of selectedTopics) {
        if (availableFree > 0) {
          availableFree--;
          freeCount++;
        } else {
          paidCount++;
        }
      }

      if (paidCount === 0) {
        await runGeneration(selectedTopics);
        return;
      }

      // Only the overage beyond free quota needs to be affordable / confirmed.
      const required = paidCount * CREDIT_COSTS.SEO_BLOG;
      const checkRes = await checkCreditsForAction(required);
      if (!checkRes.allowed) {
        setIsSubmittingGeneration(false);
        return;
      }

      setCreditConfirmAmount(required);
      setCreditConfirmBalance(checkRes.current);

      if (freeCount > 0) {
        setCreditConfirmDescription(
          `${freeCount} blog${freeCount > 1 ? 's' : ''} in your batch ${freeCount > 1 ? 'are' : 'is'} covered by your free monthly quota. The remaining ${paidCount} blog${paidCount > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_BLOG} credits each), charged upfront.`
        );
      } else {
        const limit = latestQuota?.limit || 0;
        if (limit > 0) {
          setCreditConfirmDescription(
            `You've used your free monthly blog quota. Generating ${selectedTopics.length} blog${selectedTopics.length > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_BLOG} credits each), charged upfront.`
          );
        } else {
          setCreditConfirmDescription(
            `Generating ${selectedTopics.length} blog${selectedTopics.length > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_BLOG} credits each), charged upfront.`
          );
        }
      }

      setShowCreditConfirm(true);
    } finally {
      setIsSubmittingGeneration(false);
    }
  };

  const handleConfirmBlogGeneration = async () => {
    if (isGenerating || isSubmittingGeneration) return;
    const selectedTopics = topics.filter(t => t.selected);
    setShowCreditConfirm(false);
    await runGeneration(selectedTopics);
  };

  // Step label mapping from backend ProcessingStep enum values
  const STEP_LABELS: Record<string, string> = {
    PREPARING: 'Preparing workspace...',
    RESEARCH:  'Researching topic...',
    OUTLINE:   'Building outline...',
    WRITING:   'Writing content...',
    REVIEW:    'Reviewing quality...',
    IMAGE:     'Generating cover image...',
    SAVING:    'Saving blog post...',
  };

  const startQueueJob = async (queue: any[], index: number) => {
    if (index >= queue.length) {
      localStorage.removeItem('blog_generation_queue');
      localStorage.removeItem('blog_generation_queue_index');
      setGenerationQueue([]);
      setCurrentQueueIndex(0);
      await cleanupGenerationState();
      return;
    }

    const item = queue[index];
    setIsGenerating(true);
    localStorage.setItem('blog_generation_active', 'true');
    window.dispatchEvent(new CustomEvent('platform_generation_started'));
    setJobProgress(0);
    setJobStep(`Preparing blog ${index + 1} of ${queue.length}...`);
    setGenerationProgress({ current: 0, total: 100 });
    if (index === 0) {
      setIsMinimized(false);
    }

    try {
      const res = await apiService.generateBlog({
        user_id: userId,
        topic: item.topic,
        author_id: item.authorId,
        manual_author_name: item.manualAuthorName,
        differentiation: item.differentiation,
      });

      const job = res?.data;
      if (!job?.job_id) {
        console.error('Blog generation failed to start:', res);
        alert(`Failed to start generation for topic: ${item.topic}`);
        const nextIndex = index + 1;
        setCurrentQueueIndex(nextIndex);
        localStorage.setItem('blog_generation_queue_index', nextIndex.toString());
        await startQueueJob(queue, nextIndex);
        return;
      }

      const jobId = job.job_id;
      setActiveJobId(jobId);
      localStorage.setItem('blog_generation_active_job_id', jobId);
      startPollingJob(jobId, queue, index);
    } catch (e: any) {
      console.error('Failed to start blog generation:', e);
      if (e?.status === 409 || e?.message?.includes('already running')) {
        alert('A blog is already being generated. Please wait for it to complete.');
      } else {
        alert(`Failed to start generation for topic: ${item.topic}`);
        const nextIndex = index + 1;
        setCurrentQueueIndex(nextIndex);
        localStorage.setItem('blog_generation_queue_index', nextIndex.toString());
        await startQueueJob(queue, nextIndex);
      }
    }
  };

  const startPollingJob = (jobId: string, currentQueue: any[] = generationQueue, index: number = currentQueueIndex) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const interval = setInterval(async () => {
      try {
        const statusRes = await apiService.getBlogJob(jobId, userId);
        const jobData = statusRes?.data;
        if (!jobData) return;

        const pct = jobData.progress ?? 0;
        const step = jobData.current_step ?? 'PREPARING';
        setJobProgress(pct);
        
        const queueInfo = currentQueue.length > 1 ? `[Blog ${index + 1}/${currentQueue.length}] ` : '';
        setJobStep(`${queueInfo}${STEP_LABELS[step] ?? step}`);
        setGenerationProgress({ current: pct, total: 100 });

        if (jobData.status === 'COMPLETED') {
          clearInterval(interval);
          pollIntervalRef.current = null;

          // Real-time cache invalidation and immediate refetch for instant bell badge update
          queryClient.invalidateQueries({ queryKey: ['platformNotifications'] });
          queryClient.invalidateQueries({ queryKey: ['platformNotificationsUnreadCount'] });
          queryClient.refetchQueries({ queryKey: ['platformNotifications'] });
          queryClient.refetchQueries({ queryKey: ['platformNotificationsUnreadCount'] });
          invalidateCredits();

          const completedTopicId = currentQueue[index]?.topicId;
          if (completedTopicId) {
            setCompletedTopicIds(prev => ({ ...prev, [completedTopicId]: true }));
            setFailedTopicErrors(prev => {
              const next = { ...prev };
              delete next[completedTopicId];
              return next;
            });
          }

          if (jobData.blog_id) {
            try {
              const blogRes = await apiService.getSingleBlog(jobData.blog_id, userId);
              const blog = blogRes?.data;
              if (blog) {
                setGeneratedBlogs(prev => {
                  if (prev.some(b => b.id === blog.id)) return prev;
                  return [{
                    id: blog.id,
                    topic: blog.topic,
                    author: blog.author,
                    wordCount: blog.word_count || 0,
                    readTime: blog.read_time || Math.ceil((blog.word_count || 0) / 200),
                    published: blog.published,
                    content: blog.content,
                    image_url: blog.image_url,
                    created_at: blog.created_at,
                  }, ...prev];
                });
              }
            } catch (fetchErr) {
              console.error('Failed to fetch completed blog:', fetchErr);
              await handleFetchHistory();
            }
          } else {
            await handleFetchHistory();
          }

          const nextIndex = index + 1;
          setCurrentQueueIndex(nextIndex);
          localStorage.setItem('blog_generation_queue_index', nextIndex.toString());
          await startQueueJob(currentQueue, nextIndex);

        } else if (jobData.status === 'FAILED') {
          clearInterval(interval);
          pollIntervalRef.current = null;

          const failedTopicId = currentQueue[index]?.topicId;
          const errMsg = jobData.error_message || 'Blog generation failed';
          if (failedTopicId) {
            setFailedTopicErrors(prev => ({ ...prev, [failedTopicId]: errMsg }));
          }

          queryClient.invalidateQueries({ queryKey: ['platformNotifications'] });
          queryClient.invalidateQueries({ queryKey: ['platformNotificationsUnreadCount'] });
          queryClient.refetchQueries({ queryKey: ['platformNotifications'] });
          queryClient.refetchQueries({ queryKey: ['platformNotificationsUnreadCount'] });
          invalidateCredits();
          await fetchQuota();

          const nextIndex = index + 1;
          setCurrentQueueIndex(nextIndex);
          localStorage.setItem('blog_generation_queue_index', nextIndex.toString());
          await startQueueJob(currentQueue, nextIndex);

        } else if (jobData.status === 'CANCELLED') {
          clearInterval(interval);
          pollIntervalRef.current = null;
          localStorage.removeItem('blog_generation_queue');
          localStorage.removeItem('blog_generation_queue_index');
          localStorage.removeItem('blog_generation_active');
          window.dispatchEvent(new CustomEvent('platform_generation_completed'));
          setGenerationQueue([]);
          setCurrentQueueIndex(0);
          await cleanupGenerationState();
        }
      } catch (pollErr) {
        console.error('Error polling job status:', pollErr);
      }
    }, 5000);

    pollIntervalRef.current = interval;
  };

  const checkActiveJob = async () => {
    try {
      const res = await apiService.getActiveBlogJob(userId);
      const activeJob = res?.data;
      if (activeJob && activeJob.job_id) {
        setIsGenerating(true);
        localStorage.setItem('blog_generation_active', 'true');
        localStorage.setItem('blog_generation_active_job_id', activeJob.job_id);
        window.dispatchEvent(new CustomEvent('platform_generation_started'));
        setActiveJobId(activeJob.job_id);
        const pct = activeJob.progress ?? 0;
        const step = activeJob.current_step ?? 'PREPARING';
        setJobProgress(pct);
        
        const storedQueueStr = localStorage.getItem('blog_generation_queue');
        const storedIndexStr = localStorage.getItem('blog_generation_queue_index');
        
        let queue = [{
          topic: activeJob.topic,
          authorId: null,
          manualAuthorName: '',
          differentiation: '',
          topicId: 0
        }];
        let index = 0;

        if (storedQueueStr) {
          try {
            const parsedQueue = JSON.parse(storedQueueStr);
            const parsedIndex = parseInt(storedIndexStr || '0', 10);
            if (Array.isArray(parsedQueue) && parsedQueue.length > 0) {
              queue = parsedQueue;
              index = parsedIndex;
              if (index >= queue.length) {
                index = queue.length - 1;
              }
              setGenerationQueue(queue);
              setCurrentQueueIndex(index);
            }
          } catch (jsonErr) {
            console.error('Error parsing stored queue:', jsonErr);
          }
        } else {
          setGenerationQueue(queue);
          setCurrentQueueIndex(0);
        }

        const queueInfo = queue.length > 1 ? `[Blog ${index + 1}/${queue.length}] ` : '';
        setJobStep(`${queueInfo}${STEP_LABELS[step] ?? step}`);
        startPollingJob(activeJob.job_id, queue, index);
      } else {
        localStorage.removeItem('blog_generation_queue');
        localStorage.removeItem('blog_generation_queue_index');
        localStorage.removeItem('blog_generation_active');
        localStorage.removeItem('blog_generation_active_job_id');
      }
    } catch (err) {
      console.error('Failed to check active blog job on load:', err);
    }
  };

  const handleCancelJob = () => {
    setShowCancelConfirmModal(true);
  };

  const handleConfirmCancelJob = async () => {
    setIsCancelling(true);
    let targetJobId = activeJobId || localStorage.getItem('blog_generation_active_job_id');
    
    // Fallback: if targetJobId is not in memory or localStorage, query active job from backend
    if (!targetJobId) {
      try {
        const res = await apiService.getActiveBlogJob(userId);
        targetJobId = res?.data?.job_id || null;
      } catch (_) {}
    }

    try {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (targetJobId) {
        await apiService.cancelBlogJob(targetJobId, userId);
      }
    } catch (err) {
      console.error("Failed to cancel job on backend:", err);
    } finally {
      localStorage.removeItem('blog_generation_queue');
      localStorage.removeItem('blog_generation_queue_index');
      localStorage.removeItem('blog_generation_active');
      localStorage.removeItem('blog_generation_active_job_id');
      setGenerationQueue([]);
      setCurrentQueueIndex(0);
      await cleanupGenerationState();
      setIsCancelling(false);
      setShowCancelConfirmModal(false);
    }
  };

  const cleanupGenerationState = async () => {
    localStorage.removeItem('blog_generation_active');
    localStorage.removeItem('blog_generation_active_job_id');
    window.dispatchEvent(new CustomEvent('platform_generation_completed'));
    setIsGenerating(false);
    setIsSubmittingGeneration(false);
    setActiveJobId(null);
    setJobProgress(0);
    setJobStep('Preparing...');
    setIsMinimized(false);
    setShowStartNoticeModal(false);
    setShowCustomization(false);
    setCustomizations({});
    setAuthorAssignments({});
    setManualAuthors({});
    invalidateCredits();
    queryClient.invalidateQueries({ queryKey: ['userBalance'] });
    queryClient.invalidateQueries({ queryKey: ['creditTransactions'] });
    let latestQuotaRemaining: number | undefined = undefined;
    try {
      const q = await fetchQuota();
      if (q) {
        latestQuotaRemaining = q.remaining ?? 0;
      }
    } catch (_) {}
    try {
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;
      await checkLowCreditWarning(current, latestQuotaRemaining);
    } catch (_) {}
    try {
      await fetchBlogs();
    } catch (_) {}
  };

  const handleRetryTopic = async (topic: BlogTopic) => {
    setFailedTopicErrors(prev => {
      const next = { ...prev };
      delete next[topic.id];
      return next;
    });
    setTopics(prev => prev.map(t => t.id === topic.id ? { ...t, selected: true } : t));
    await runGeneration([{ ...topic, selected: true }]);
  };

  const runGeneration = async (selectedTopics: BlogTopic[]) => {
    // Guard: Deduplicate selected topics by normalized title
    const seenTitles = new Set<string>();
    const uniqueSelected: BlogTopic[] = [];
    selectedTopics.forEach(topic => {
      const norm = topic.title.trim().toLowerCase();
      if (norm && !seenTitles.has(norm)) {
        seenTitles.add(norm);
        uniqueSelected.push(topic);
      }
    });

    if (uniqueSelected.length === 0) {
      setIsSubmittingGeneration(false);
      return;
    }
    trackAction(ACTIONS.GENERATE_CONTENT, 'Generate Blogs', { selected_topic_count: uniqueSelected.length });

    const queue = uniqueSelected.map(topic => {
      const authorId = authorAssignments[topic.id];
      const cust = customizations[topic.id];
      let diff = '';
      if (cust?.usp) diff += `USP: ${cust.usp}\n`;
      if (cust?.anecdote) diff += `Anecdote: ${cust.anecdote}`;

      return {
        topic: topic.title,
        authorId,
        manualAuthorName: manualAuthors[topic.id] || '',
        differentiation: diff,
        topicId: topic.id
      };
    });

    setGenerationQueue(queue);
    setCurrentQueueIndex(0);
    localStorage.setItem('blog_generation_queue', JSON.stringify(queue));
    localStorage.setItem('blog_generation_queue_index', '0');
    localStorage.setItem('blog_generation_active', 'true');
    window.dispatchEvent(new CustomEvent('platform_generation_started'));
    setShowCustomization(false);
    setShowStartNoticeModal(true);

    await startQueueJob(queue, 0);
  };

  const [showCustomization, setShowCustomization] = useState(false);
  const [customizations, setCustomizations] = useState<Record<number, { usp: string; anecdote: string }>>({});
  const [authorAssignments, setAuthorAssignments] = useState<Record<number, number | null>>({});
  const [manualAuthors, setManualAuthors] = useState<Record<number, string>>({});

  const fetchQuota = async () => {
    try {
      const data = await apiService.getBlogQuota(userId);
      // The API returns the quota object directly in data.data
      const q = data.data.quota || data.data;
      setQuota(q);
      return q;
    } catch (e) {
      console.error("Failed to fetch quota", e);
      return null;
    }
  };

  // 1. Fetch Authors and Discover Topics on Mount
  useEffect(() => {
    if (userId) {
      const loadAllData = async () => {
        setIsInitialLoading(true);
        await Promise.all([
          fetchAuthors(),
          handleDiscoverTopics(),
          handleFetchHistory(),
          fetchQuota(),
          checkActiveJob()
        ]);
        setIsInitialLoading(false);
      };
      loadAllData();
    }
  }, [userId]);

  // Listen for tab navigation events from notifications / header
  useEffect(() => {
    const handleTabNav = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        if (customEvent.detail.subtab === 'generated') {
          setActiveTab('generated');
          handleFetchHistory();
        } else if (customEvent.detail.subtab === 'discovery') {
          setActiveTab('discovery');
        }
      }
    };
    window.addEventListener('navigate_to_tab', handleTabNav);
    return () => window.removeEventListener('navigate_to_tab', handleTabNav);
  }, []);

  // Escape key closes the one-time start notice modal
  useEffect(() => {
    if (!showStartNoticeModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowStartNoticeModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showStartNoticeModal]);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleFetchHistory = async () => {
    try {
      const data = await apiService.getBlogHistory(userId);
      const history = (data.data?.blogs || []).map((b: any) => ({
        id: b.id,
        topic: b.topic,
        author: b.author,
        wordCount: b.word_count,
        readTime: b.read_time,
        published: b.published,
        content: b.content,
        image_url: b.image_url,
        created_at: b.created_at
      }));
      setGeneratedBlogs(history);
    } catch (e) {
      console.error("Failed to fetch blog history", e);
    }
  };

  const fetchAuthors = async () => {
    setLoadingAuthors(true);
    try {
      const data = await apiService.getBlogAuthors(userId);
      const backendAuthors = data.data?.authors || [];
      const uniqueAuthors: any[] = [];
      const seenNames = new Set<string>();
      for (const author of backendAuthors) {
        const lowerName = author.name.toLowerCase().trim();
        if (!seenNames.has(lowerName)) {
          seenNames.add(lowerName);
          uniqueAuthors.push(author);
        }
      }
      setAuthors(uniqueAuthors);
      // Default assignment if authors exist
      if (backendAuthors.length > 0 && topics.length > 0) {
        // Logic to default assign could go here, but better to do it when topics load or user selects
      }
    } catch (e) {
      console.error("Failed to fetch authors", e);
    } finally {
      setLoadingAuthors(false);
    }
  };

  // 2. Discover Topics (Auto using Keyword Universe)
  const handleDiscoverTopics = async (force?: boolean) => {
    setLoadingTopics(true);
    try {
      // No seed_keyword passed, backend uses Keyword Universe
      const data = await apiService.discoverBlogTopics(userId, force === true);
      const rawTopics = data.data?.topics || [];
      const seenTitles = new Set<string>();
      const newTopics: BlogTopic[] = [];

      rawTopics.forEach((t: any, index: number) => {
        const title = (t.title || '').trim();
        const norm = title.toLowerCase();
        if (norm && !seenTitles.has(norm)) {
          seenTitles.add(norm);
          newTopics.push({
            ...t,
            title,
            id: t.id || (Date.now() + index),
            selected: false,
          });
        }
      });

      setTopics(newTopics);
      setCustomizations({});
      setAuthorAssignments({});
      setManualAuthors({});
    } catch (e) {
      console.error("Failed to discover topics", e);
    } finally {
      setLoadingTopics(false);
    }
  };

  // Selection Logic
  const selectedCount = topics.filter(t => t.selected).length;

  const handleTopicToggle = (id: number) => {
    setTopics(topics.map(topic => {
      if (topic.id === id) {
        // Ignore Quota Limit entirely for selection
        if (!topic.selected) {
            // Allow unlimited selection
        }
        return { ...topic, selected: !topic.selected };
      }
      return topic;
    }));
  };

  const handleCustomizationChange = (topicId: number, field: 'usp' | 'anecdote', value: string) => {
    setCustomizations({
      ...customizations,
      [topicId]: {
        ...customizations[topicId],
        [field]: value
      }
    });
  };

  const handleAuthorAssignment = (topicId: number, authorId: number | null) => {
    setAuthorAssignments({
      ...authorAssignments,
      [topicId]: authorId
    });
    if (authorId !== null) {
      setManualAuthors({
        ...manualAuthors,
        [topicId]: ''
      });
    }
  };

  const handleConfirmSelection = () => {
    if (isGenerating || isSubmittingGeneration) return;
    setShowCustomization(true);
    // Pre-assign default author (first one) to all selected topics if not set
    const selectedTopics = topics.filter(t => t.selected);
    const newAssignments = { ...authorAssignments };
    selectedTopics.forEach(t => {
      if (!newAssignments[t.id] && authors.length > 0) {
        newAssignments[t.id] = authors[0].id;
      }
    });
    setAuthorAssignments(newAssignments);
  };



  // Helpers
  const getDifficultyColor = (difficulty: number) => {
    if (difficulty < 50) return 'text-green-600 bg-green-100';
    if (difficulty < 70) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getCompetitionColor = (competition: string) => {
    if (competition === 'Low') return 'text-green-600 bg-green-100';
    if (competition === 'Medium') return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  // Edit & Download Logic
  const [editingBlog, setEditingBlog] = useState<GeneratedBlog | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editMode, setEditMode] = useState<'write' | 'preview'>('write');
  const [isSavingBlog, setIsSavingBlog] = useState(false);

  interface DocSpan {
    text: string;
    bold?: boolean;
    italic?: boolean;
    highlight?: boolean;
  }

  interface DocBlock {
    type: 'heading' | 'paragraph' | 'blockquote' | 'list';
    text?: string;
    depth?: number;
    ordered?: boolean;
    items?: string[];
    spans?: DocSpan[];
    listSpans?: DocSpan[][];
  }

  const decodeHtmlEntities = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  };

  const cleanMarkdownInline = (text: string, trim: boolean = true): string => {
    if (!text) return '';
    const cleaned = decodeHtmlEntities(text)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url) -> link
      .replace(/\*\*([^*]+)\*\*/g, '$1')        // **bold** -> bold
      .replace(/\*([^*]+)\*/g, '$1')            // *italic* -> italic
      .replace(/__([^_]+)__/g, '$1')            // __bold__ -> bold
      .replace(/_([^_]+)_/g, '$1')              // _italic_ -> italic
      .replace(/~~([^~]+)~~/g, '$1')            // ~~strike~~ -> strike
      .replace(/<u>([^<]+)<\/u>/gi, '$1')       // <u>underline</u> -> underline
      .replace(/`([^`]+)`/g, '$1')              // `code` -> code
      .replace(/<[^>]+>/g, '')                  // Strip ALL HTML tags (<span ...>, </span>, <strong ...>, etc.)
      .replace(/^#+\s*/, '');                   // leading #
    return trim ? cleaned.trim() : cleaned;
  };

  const sanitizeForPdf = (str: string | undefined | null): string => {
    if (!str) return '';
    return str
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[\u2026]/g, '...')
      .replace(/[\u2022\u25CF\u25CB\u25AA\u25AB]/g, '-')
      .replace(/[\u00A0]/g, ' ')
      .replace(/[^\x20-\x7E\t\n\r]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseInlineSpans = (rawText: string): DocSpan[] => {
    if (!rawText) return [];

    // 1. Normalize markdown inline syntax to standard HTML tags for unified DOM parsing
    let html = rawText
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url) -> link text
      .replace(/`([^`]+)`/g, '$1')              // `code` -> code text
      .replace(/~~([^~]+)~~/g, '$1')            // ~~strike~~ -> strike text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // **bold** -> <strong>bold</strong>
      .replace(/__([^_]+)__/g, '<strong>$1</strong>')     // __bold__ -> <strong>bold</strong>
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>') // *italic* -> <em>italic</em>
      .replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');    // _italic_ -> <em>italic</em>

    // 2. Parse using browser DOMParser to strip all tags while tracking bold/italic state
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const spans: DocSpan[] = [];

    const walk = (node: Node, isBold: boolean, isItalic: boolean) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const rawTxt = node.textContent || '';
        const txt = decodeHtmlEntities(rawTxt);
        if (txt) {
          spans.push({
            text: txt,
            bold: isBold,
            italic: isItalic,
            highlight: isBold,
          });
        }
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        const boldHere = isBold || tag === 'strong' || tag === 'b' || tag === 'mark';
        const italicHere = isItalic || tag === 'em' || tag === 'i';

        node.childNodes.forEach((child) => {
          walk(child, boldHere, italicHere);
        });
      }
    };

    walk(doc.body.firstChild || doc.body, false, false);

    if (spans.length === 0) {
      const fallbackText = cleanMarkdownInline(rawText, false);
      if (fallbackText) {
        spans.push({ text: fallbackText, bold: false, highlight: false });
      }
    }

    return spans;
  };

  const extractDocumentBlocks = (content: string): DocBlock[] => {
    if (!content) return [];
    const raw = content.trim();

    // If content is HTML
    if (/^<[a-z][\s\S]*>/i.test(raw)) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, 'text/html');
      const blocks: DocBlock[] = [];

      Array.from(doc.body.children).forEach((el) => {
        const tag = el.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag)) {
          blocks.push({
            type: 'heading',
            depth: parseInt(tag.charAt(1)),
            text: cleanMarkdownInline(el.textContent || ''),
          });
        } else if (tag === 'p' || tag === 'div') {
          const span = el.querySelector('span[style*="font-size"]') as HTMLElement;
          const sz = span ? parseInt(span.style.fontSize) : 0;
          const txt = cleanMarkdownInline(el.textContent || '');
          const innerHtml = el.innerHTML || '';
          if (sz >= 20 && txt) {
            blocks.push({
              type: 'heading',
              depth: sz >= 28 ? 1 : sz >= 24 ? 2 : 3,
              text: txt,
            });
          } else if (txt) {
            blocks.push({
              type: 'paragraph',
              text: txt,
              spans: parseInlineSpans(innerHtml),
            });
          }
        } else if (tag === 'blockquote') {
          const txt = cleanMarkdownInline(el.textContent || '');
          if (txt) {
            blocks.push({
              type: 'blockquote',
              text: txt,
              spans: parseInlineSpans(el.innerHTML || ''),
            });
          }
        } else if (tag === 'ol' || tag === 'ul') {
          const lis = Array.from(el.querySelectorAll('li'));
          const items = lis.map((li) => cleanMarkdownInline(li.textContent || '')).filter(Boolean);
          const listSpans = lis.map((li) => parseInlineSpans(li.innerHTML || '')).filter((s) => s.length > 0);
          if (items.length > 0) {
            blocks.push({
              type: 'list',
              ordered: tag === 'ol',
              items,
              listSpans,
            });
          }
        } else {
          const txt = cleanMarkdownInline(el.textContent || '');
          if (txt) {
            blocks.push({
              type: 'paragraph',
              text: txt,
              spans: parseInlineSpans(el.innerHTML || ''),
            });
          }
        }
      });

      return blocks;
    }

    // Otherwise, parse as Markdown
    const normalized = raw
      .replace(/([^\n])\n(>\s*|\*|\-|\d+\.|\#{1,6}\s+)/g, '$1\n\n$2')
      .replace(/>\s*\^\s*#+\s*#+/g, '> ## ')
      .replace(/(?:^|\n)#+\s*#+\s*/g, '\n## ');

    const tokens = marked.lexer(normalized);
    const blocks: DocBlock[] = [];

    tokens.forEach((token: any) => {
      if (token.type === 'heading') {
        blocks.push({
          type: 'heading',
          depth: token.depth || 2,
          text: cleanMarkdownInline(token.text),
        });
      } else if (token.type === 'paragraph' || token.type === 'html' || token.type === 'text') {
        const rawP = token.text || token.raw || '';
        const txt = cleanMarkdownInline(rawP);
        if (txt) {
          blocks.push({
            type: 'paragraph',
            text: txt,
            spans: parseInlineSpans(rawP),
          });
        }
      } else if (token.type === 'blockquote') {
        if (token.tokens && token.tokens.length > 0) {
          token.tokens.forEach((sub: any) => {
            if (sub.type === 'list') {
              const items = (sub.items || []).map((it: any) => cleanMarkdownInline(it.text)).filter(Boolean);
              const listSpans = (sub.items || []).map((it: any) => parseInlineSpans(it.text || it.raw || '')).filter((s: any) => s.length > 0);
              if (items.length > 0) {
                blocks.push({
                  type: 'blockquote',
                  text: items.map((it: string, idx: number) => `${sub.ordered ? `${idx + 1}. ` : '• '}${it}`).join('\n'),
                  listSpans,
                });
              }
            } else {
              const rawB = sub.text || sub.raw || '';
              const txt = cleanMarkdownInline(rawB);
              if (txt) {
                blocks.push({
                  type: 'blockquote',
                  text: txt,
                  spans: parseInlineSpans(rawB),
                });
              }
            }
          });
        } else {
          const rawB = token.text || token.raw || '';
          const txt = cleanMarkdownInline(rawB);
          if (txt) {
            blocks.push({
              type: 'blockquote',
              text: txt,
              spans: parseInlineSpans(rawB),
            });
          }
        }
      } else if (token.type === 'list') {
        const items = (token.items || []).map((item: any) => cleanMarkdownInline(item.text)).filter(Boolean);
        const listSpans = (token.items || []).map((item: any) => parseInlineSpans(item.text || item.raw || '')).filter((s: any) => s.length > 0);
        if (items.length > 0) {
          blocks.push({
            type: 'list',
            ordered: !!token.ordered,
            items,
            listSpans,
          });
        }
      }
    });

    return blocks;
  };

  const handleDownloadDocx = async (blog: GeneratedBlog) => {
    trackAction(ACTIONS.DOWNLOAD, 'Download blog DOCX', { blog_id: blog.id, format: 'docx' });
    setDownloadingDocxBlogId(blog.id);
    const toastId = toast.loading('Preparing Word document...');
    try {
      const blocks = extractDocumentBlocks(blog.content || '');

      const children: Paragraph[] = [];

      // Title & Author
      children.push(new Paragraph({
        text: blog.topic || 'Untitled Blog',
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      }));
      children.push(new Paragraph({
        text: `By ${blog.author || 'Author'}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
        style: "Subtitle"
      }));

      // Content
      blocks.forEach((block) => {
        if (block.type === 'heading') {
          const level = block.depth === 1 ? HeadingLevel.HEADING_1 :
            block.depth === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3;
          children.push(new Paragraph({
            text: block.text || '',
            heading: level,
            spacing: { before: 200, after: 100 }
          }));
        } else if (block.type === 'paragraph') {
          const spans = block.spans && block.spans.length > 0 ? block.spans : [{ text: block.text || '', bold: false, highlight: false }];
          const runs = spans.map((span) => new TextRun({
            text: span.text,
            bold: !!span.bold,
            highlight: span.highlight ? 'yellow' : undefined,
          }));
          children.push(new Paragraph({
            children: runs,
            spacing: { after: 100 }
          }));
        } else if (block.type === 'blockquote') {
          const spans = block.spans && block.spans.length > 0 ? block.spans : [{ text: block.text || '', bold: false, highlight: false }];
          const runs = spans.map((span) => new TextRun({
            text: span.text,
            italics: true,
            bold: !!span.bold,
            highlight: span.highlight ? 'yellow' : undefined,
          }));
          children.push(new Paragraph({
            children: runs,
            indent: { left: 720 }, // 0.5 inch
            border: {
              left: {
                color: "3B82F6",
                size: 24,
                space: 12,
                value: "single" as any
              }
            },
            spacing: { before: 100, after: 150 }
          }));
        } else if (block.type === 'list' && block.items) {
          block.items.forEach((item, idx) => {
            const spans = block.listSpans && block.listSpans[idx] ? block.listSpans[idx] : [{ text: item, bold: false, highlight: false }];
            const prefix = block.ordered ? `${idx + 1}. ` : `- `;
            const runs: TextRun[] = [new TextRun({ text: prefix })];
            spans.forEach((span) => {
              runs.push(new TextRun({
                text: span.text,
                bold: !!span.bold,
                highlight: span.highlight ? 'yellow' : undefined,
              }));
            });
            children.push(new Paragraph({
              children: runs,
              indent: { left: 720 },
              spacing: { after: 50 }
            }));
          });
        }
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${(blog.topic || 'blog').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`);
      toast.success('Word document downloaded!', { id: toastId });
    } catch (err) {
      console.error('Failed to download blog DOCX:', err);
      toast.error('Failed to generate Word document. Please try again.', { id: toastId });
    } finally {
      setDownloadingDocxBlogId(null);
    }
  };

  const handleDownloadPDF = async (blog: GeneratedBlog) => {
    trackAction(ACTIONS.DOWNLOAD, 'Download blog PDF', { blog_id: blog.id, format: 'pdf' });
    setDownloadingPdfBlogId(blog.id);
    const toastId = toast.loading('Generating PDF...');
    try {
      const jspdfModule = await import('jspdf');
      const jsPDF = jspdfModule.jsPDF || (jspdfModule as any).default || jspdfModule;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const blocks = extractDocumentBlocks(blog.content || '');

      // Formatting
      const margin = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const maxLineWidth = pageWidth - margin * 2;
      let y = 20;

      const checkPageBreak = (heightNeeded: number) => {
        if (y + heightNeeded > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Title
      const rawTitle = blog.topic || 'Untitled Blog';
      const safeTitle = sanitizeForPdf(rawTitle) || 'Untitled Blog';
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      const titleLines = doc.splitTextToSize(safeTitle, maxLineWidth);
      doc.text(titleLines, margin, y);
      y += Math.max(10, titleLines.length * 8) + 2;

      // Author
      const rawAuthor = blog.author || 'Author';
      const safeAuthor = sanitizeForPdf(rawAuthor) || 'Author';
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 116, 139);
      doc.text(`By ${safeAuthor}`, margin, y);
      y += 8;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;

      // Content Processing
      doc.setFont("helvetica", "normal");

      const renderSpans = (
        spans: DocSpan[],
        startX: number,
        maxWidth: number,
        lineHeight: number,
        fontSize: number,
        isItalic: boolean = false
      ) => {
        doc.setFontSize(fontSize);
        interface WordToken {
          text: string;
          bold: boolean;
          highlight: boolean;
          isSpace: boolean;
        }
        const tokens: WordToken[] = [];
        spans.forEach((span) => {
          const cleanText = sanitizeForPdf(span.text);
          if (!cleanText) return;
          const pieces = cleanText.split(/(\s+)/);
          pieces.forEach((p) => {
            if (!p) return;
            tokens.push({
              text: p,
              bold: !!span.bold,
              highlight: !!span.highlight,
              isSpace: /^\s+$/.test(p),
            });
          });
        });

        let curX = startX;
        tokens.forEach((tok) => {
          if (curX === startX && tok.isSpace) {
            return;
          }
          const fontStyle = tok.bold && isItalic ? "bolditalic" : tok.bold ? "bold" : isItalic ? "italic" : "normal";
          doc.setFont("helvetica", fontStyle);
          const w = doc.getTextWidth(tok.text);

          if (curX + w > startX + maxWidth && !tok.isSpace) {
            curX = startX;
            y += lineHeight;
            checkPageBreak(lineHeight + 4);
          }

          if (!tok.isSpace && tok.highlight) {
            doc.setFillColor(254, 240, 138); // Soft yellow keyword highlight #FEF08A
            const highlightW = Math.max(1, w + 1);
            const highlightH = Math.max(1, fontSize * 0.45);
            const rx = Math.min(0.8, highlightW / 2, highlightH / 2);
            doc.roundedRect(curX - 0.5, y - (fontSize * 0.35), highlightW, highlightH, rx, rx, 'F');
            doc.setTextColor(15, 23, 42); // Dark slate text for highlighted keyword
          } else if (isItalic) {
            doc.setTextColor(75, 85, 99);
          } else {
            doc.setTextColor(30, 41, 59);
          }

          doc.setFont("helvetica", fontStyle);
          doc.text(tok.text, curX, y);
          curX += w;
        });

        y += lineHeight + 3;
      };

      blocks.forEach((block) => {
        if (block.type === 'heading') {
          checkPageBreak(15);
          doc.setFont("helvetica", "bold");
          const size = block.depth === 1 ? 15 : block.depth === 2 ? 13 : 11;
          doc.setFontSize(size);
          doc.setTextColor(15, 23, 42);
          const cleanHeading = sanitizeForPdf(block.text || '');
          const lines = doc.splitTextToSize(cleanHeading, maxLineWidth);
          doc.text(lines, margin, y);
          y += Math.max(6, lines.length * (size * 0.45)) + 4;
          doc.setFont("helvetica", "normal");
        } else if (block.type === 'paragraph') {
          const spans = block.spans && block.spans.length > 0 ? block.spans : [{ text: block.text || '', bold: false, highlight: false }];
          checkPageBreak(12);
          renderSpans(spans, margin, maxLineWidth, 6, 10.5, false);
        } else if (block.type === 'blockquote') {
          const spans = block.spans && block.spans.length > 0 ? block.spans : [{ text: block.text || '', bold: false, highlight: false }];
          checkPageBreak(15);
          const startY = y;
          renderSpans(spans, margin + 5, maxLineWidth - 10, 6, 10.5, true);

          // Draw left blue accent line
          if (startY <= y) {
            doc.setDrawColor(59, 130, 246);
            doc.setLineWidth(1.2);
            doc.line(margin, startY - 3, margin, y - 2);
          }
        } else if (block.type === 'list' && block.items) {
          block.items.forEach((item, idx) => {
            const spans = block.listSpans && block.listSpans[idx] ? block.listSpans[idx] : [{ text: item, bold: false, highlight: false }];
            const prefix = block.ordered ? `${idx + 1}. ` : `- `;
            const fullSpans: DocSpan[] = [{ text: prefix, bold: true, highlight: false }, ...spans];
            checkPageBreak(8);
            renderSpans(fullSpans, margin + 5, maxLineWidth - 5, 6, 10.5, false);
          });
        }
        y += 2;
      });

      const cleanFileName = (safeTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'blog') + '.pdf';
      try {
        const blob = doc.output('blob');
        saveAs(blob, cleanFileName);
      } catch {
        doc.save(cleanFileName);
      }

      toast.success('Blog PDF downloaded successfully!', { id: toastId });
    } catch (err) {
      console.error('Failed to generate/download blog PDF:', err);
      toast.error('Failed to generate PDF. Please try again.', { id: toastId });
    } finally {
      setDownloadingPdfBlogId(null);
    }
  };

  const handleEdit = (blog: GeneratedBlog) => {
    trackAction(ACTIONS.EDIT_CONTENT, 'Open blog for editing', { blog_id: blog.id });
    setEditingBlog(blog);
    let raw = (blog.content || '').trim();

    // 1. If it's already HTML (e.g. starts with <p> or <h or <div or <blockquote>), use directly
    if (/^<[a-z][\s\S]*>/i.test(raw)) {
      setEditContent(raw);
      setEditMode('write');
      return;
    }

    // 2. Clean up any legacy corrupted heading/blockquote artifacts from previous bad conversions
    raw = raw
      .replace(/>\s*\^\s*#+\s*#+/g, '> ## ')
      .replace(/(?:^|\n)#+\s*#+\s*/g, '\n## ');

    // 3. Ensure markdown block indicators (> , * , - , # , 1. ) always have a blank line before them
    // so marked.parse treats them as separate block elements rather than lazy paragraph continuations
    raw = raw.replace(/([^\n])\n(>\s*|\*|\-|\d+\.|\#{1,6}\s+)/g, '$1\n\n$2');

    // 4. Configure marked with breaks: true and gfm: true
    marked.setOptions({
      breaks: true,
      gfm: true,
    });

    const html = marked.parse(raw) as string;
    setEditContent(html);
    setEditMode('write');
  };

  // Force-download the blog cover image.
  // We use a dedicated backend endpoint that sets Content-Disposition: attachment
  // to avoid all CORS fetch issues and force a native browser download.
  const handleDownloadImage = async (blog: GeneratedBlog) => {
    let src = imgFallbacks[blog.id] || getFullImageUrl(blog.image_url);
    if (!src) return;
    
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001/api/v1').replace(/\/$/, '');
    const fallbackName = blog.topic ? `${blog.topic.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_cover.png` : 'blog_cover.png';
    
    let downloadUrl = '';
    
    // Proxy ALL image downloads through the backend to bypass CORS and force Content-Disposition
    if (blog.image_url && blog.image_url.startsWith('/generated_images/')) {
      const filename = blog.image_url.split('/').pop();
      downloadUrl = `${baseUrl}/blog/download-image?filename=${filename}`;
    } else {
      downloadUrl = `${baseUrl}/blog/download-image?url=${encodeURIComponent(src)}&filename=${encodeURIComponent(fallbackName)}`;
    }
    
    window.open(downloadUrl, '_self'); // Triggers native download via Content-Disposition
  };

  const handleSaveEdit = async () => {
    if (!editingBlog || isSavingBlog) return;
    trackAction(ACTIONS.EDIT_CONTENT, 'Save blog changes', { blog_id: editingBlog.id });
    setIsSavingBlog(true);

    try {
      const cleanHtml = editContent.trim();
      const data = await apiService.updateBlogContent(editingBlog.id, userId, cleanHtml);
      const updatedBlog = data.data;

      // Calculate clean word count and read time from the HTML text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanHtml;
      const text = (tempDiv.textContent || '').trim();
      const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;
      const readTime = Math.max(1, Math.ceil(wordCount / 200));
      
      setGeneratedBlogs(generatedBlogs.map(b =>
        b.id === editingBlog.id ? { 
          ...b, 
          content: cleanHtml,
          wordCount: updatedBlog?.word_count || wordCount,
          readTime: updatedBlog?.read_time || readTime
        } : b
      ));
      setEditingBlog(null);
      setEditContent('');
      setEditMode('write');
    } catch (e) {
      console.error("Error saving blog content", e);
      alert("Failed to save blog changes. Please try again.");
    } finally {
      setIsSavingBlog(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="mb-4">
          <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
          <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <div className="h-10 w-80 bg-gray-200 dark:bg-gray-700 rounded-full mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4"></div>
              <div className="h-6 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={onBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
        <div className="flex items-center gap-3">
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Blog Content Generator
          </h2>
          <button
            className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative"
            onClick={(e) => {
              e.stopPropagation();
              const t = document.getElementById('blog-seo-tooltip');
              if (t) {
                t.classList.toggle('opacity-0');
                t.classList.toggle('opacity-100');
                setTimeout(() => {
                  t.classList.add('opacity-0');
                  t.classList.remove('opacity-100');
                }, 4000);
              }
            }}
          >
            <i className="ri-information-line text-lg"></i>
            <div
              id="blog-seo-tooltip"
              className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-blue-600 dark:text-blue-400"></i>
                Blog Workflow
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Discover trending topics, customize with your insights, assign authors, and generate SEO-optimized posts.
              </p>
            </div>
          </button>
        </div>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Create SEO-optimized blog content based on top-ranking keywords
        </p>
      </div>

      {/* Tab Switcher */}
      <div className={`inline-flex rounded-full p-1 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <button
          onClick={() => setActiveTab('discovery')}
          disabled={isGenerating}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'discovery'
            ? 'bg-blue-600 text-white shadow-lg'
            : darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Topic Discovery
        </button>
        <button
          onClick={() => setActiveTab('generated')}
          disabled={isGenerating}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'generated'
            ? 'bg-blue-600 text-white shadow-lg'
            : darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Generated Blogs ({generatedBlogs.length})
        </button>
      </div>

      {/* Topic Discovery Tab */}
      {activeTab === 'discovery' && !showCustomization && (
        <div className="space-y-6">
          {/* Header & Refresh */}
          <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Recommended Topics from Your Keyword Universe
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  We've analyzed your high-value keywords to suggest these blog topics.
                </p>
              </div>
              <button
                onClick={() => handleDiscoverTopics(true)}
                disabled={loadingTopics}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-2 ${loadingTopics
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  }`}
              >
                {loadingTopics ? <Loader2 className="animate-spin w-4 h-4" /> : <i className="ri-refresh-line"></i>}
                Refresh Topics
              </button>
            </div>
          </div>

          {/* Topics List */}
          {topics.length > 0 && (
            <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Recommended Topics</h3>
                <div className="flex items-center gap-4">
                  {quota && (
                    quota.limit > 0 ? (
                      quota.remaining > 0 ? (
                        <span className="text-sm font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-1 rounded-full border border-emerald-200 dark:border-emerald-700 flex items-center gap-1.5">
                          <i className="ri-gift-line"></i>
                          {quota.used} of {quota.limit} free blogs generated ({quota.remaining} remaining)
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700 flex items-center gap-1.5">
                          <i className="ri-error-warning-line"></i>
                          {quota.limit} free blogs quota exhausted — Generating blogs uses {CREDIT_COSTS.SEO_BLOG} credits each.
                        </span>
                      )
                    ) : (
                      <span className="text-sm font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700 flex items-center gap-1.5">
                        <i className="ri-information-line"></i>
                        Generating blogs uses {CREDIT_COSTS.SEO_BLOG} credits each.
                      </span>
                    )
                  )}
                  <span className={`text-sm font-bold ${selectedCount > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                    {selectedCount} Selected
                  </span>
                </div>
              </div>
              <div className="space-y-3 mt-4">
                {topics.map((topic) => {
                  const isCurrentGenerating = isGenerating && (
                    generationQueue[currentQueueIndex]?.topicId === topic.id ||
                    (generationQueue[currentQueueIndex]?.topic && generationQueue[currentQueueIndex]?.topic.trim().toLowerCase() === topic.title.trim().toLowerCase())
                  );
                  const isQueued = isGenerating && !isCurrentGenerating && generationQueue.some((q, idx) =>
                    idx > currentQueueIndex && (q.topicId === topic.id || (q.topic && q.topic.trim().toLowerCase() === topic.title.trim().toLowerCase()))
                  );
                  const isCompleted = completedTopicIds[topic.id] || false;
                  const failureError = failedTopicErrors[topic.id];

                  return (
                    <div
                      key={topic.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        isCurrentGenerating
                          ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 shadow-sm'
                          : isCompleted
                          ? 'border-green-500/50 bg-green-50/40 dark:bg-green-900/10'
                          : failureError
                          ? 'border-red-400 bg-red-50/40 dark:bg-red-900/10'
                          : isQueued
                          ? 'border-amber-400/60 bg-amber-50/40 dark:bg-amber-900/10'
                          : topic.selected
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                          : darkMode
                          ? 'border-gray-700 bg-gray-700/50 hover:border-gray-600 cursor-pointer'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!isCurrentGenerating && !isQueued && !isGenerating) {
                          handleTopicToggle(topic.id);
                        }
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Left Checkbox / Status Icon */}
                        <div className="flex-shrink-0 mt-1">
                          {isCurrentGenerating ? (
                            <div className="w-5 h-5 rounded flex items-center justify-center text-blue-600">
                              <Loader2 className="w-5 h-5 animate-spin" />
                            </div>
                          ) : isCompleted ? (
                            <div className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center">
                              <i className="ri-check-line text-xs font-bold" />
                            </div>
                          ) : failureError ? (
                            <div className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center">
                              <i className="ri-close-line text-xs font-bold" />
                            </div>
                          ) : isQueued ? (
                            <div className="w-5 h-5 rounded flex items-center justify-center text-amber-500">
                              <i className="ri-time-line text-base animate-pulse" />
                            </div>
                          ) : (
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                topic.selected
                                  ? 'bg-blue-600 border-blue-600'
                                  : darkMode ? 'border-gray-600' : 'border-gray-300'
                              }`}
                            >
                              {topic.selected && <i className="ri-check-line text-white text-xs" />}
                            </div>
                          )}
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'} leading-snug`}>
                              {topic.title}
                            </h4>

                            {/* Completed / Queued / Failed status tags */}
                            {isCompleted && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 flex items-center gap-1">
                                  <i className="ri-checkbox-circle-fill text-green-600" /> Generated ✓
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('generated');
                                  }}
                                  className="px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1 shadow-xs"
                                >
                                  View Blog <i className="ri-arrow-right-line" />
                                </button>
                              </div>
                            )}

                            {isQueued && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 flex items-center gap-1">
                                  <i className="ri-time-line" /> Queued in line
                                </span>
                              </div>
                            )}

                            {failureError && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 flex items-center gap-1" title={failureError}>
                                  <i className="ri-error-warning-fill text-red-500" /> Generation Failed
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetryTopic(topic);
                                  }}
                                  className="px-2.5 py-1 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                                >
                                  <i className="ri-refresh-line" /> Retry
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Live Inline Progress Bar for currently generating topic */}
                          {isCurrentGenerating && (
                            <div className="mt-2.5 mb-2.5 p-3 bg-white dark:bg-gray-800/90 rounded-lg border border-blue-200 dark:border-blue-900/40 shadow-xs">
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    {jobStep}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-bold text-gray-700 dark:text-gray-200">
                                    {jobProgress}%
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelJob();
                                    }}
                                    className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors cursor-pointer"
                                    title="Cancel generation and refund credits"
                                  >
                                    <i className="ri-close-circle-line text-sm" />
                                    <span>Cancel</span>
                                  </button>
                                </div>
                              </div>

                              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500 ease-out"
                                  style={{ width: `${jobProgress}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Metric stats */}
                          <div className="flex items-center space-x-4 text-xs mt-1">
                            <div className="flex items-center space-x-1">
                              <i className="ri-search-line text-gray-500"></i>
                              <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                                {topic.searchVolume.toLocaleString()} searches/mo
                              </span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCompetitionColor(topic.competition)}`}>
                              {topic.competition} Comp.
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(topic.difficulty)}`}>
                              Dif: {topic.difficulty}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleConfirmSelection}
                  disabled={selectedCount === 0 || isGenerating || isSubmittingGeneration}
                  className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                    selectedCount > 0 && !isGenerating && !isSubmittingGeneration
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg cursor-pointer'
                      : darkMode
                      ? 'bg-gray-800 text-gray-500 border border-gray-700 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Generating Blogs...
                    </span>
                  ) : (
                    'Confirm Selection & Customize'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customization Panel */}
      {activeTab === 'discovery' && showCustomization && (
        <div className="space-y-6">
          {/* Generation Progress Overlay is now rendered globally at the bottom */}


          <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <i className="ri-edit-line text-xl text-blue-600"></i>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Customize Your Blogs
                </h3>
              </div>
              <button
                onClick={() => setShowCustomization(false)}
                disabled={isGenerating}
                className={`text-sm ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} cursor-pointer`}
              >
                ← Back to Topics
              </button>
            </div>

            <div className="space-y-6">
              {topics.filter(t => t.selected).map((topic) => (
                <div key={topic.id} className={`p-5 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <h4 className={`font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {topic.title}
                  </h4>

                  {/* Author Assignment */}
                  <div className="mb-4">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 block`}>
                      Assign Author
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Enter author name"
                        value={manualAuthors[topic.id] || ''}
                        onChange={(e) => {
                          setManualAuthors({ ...manualAuthors, [topic.id]: e.target.value });
                          handleAuthorAssignment(topic.id, null);
                        }}
                        className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                          manualAuthors[topic.id]
                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100'
                            : darkMode
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                  </div>

                  {/* USP Input */}
                  <div className="mb-4">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 block`}>
                      Unique Selling Points (USP)
                    </label>
                    <textarea
                      value={customizations[topic.id]?.usp || ''}
                      onChange={(e) => handleCustomizationChange(topic.id, 'usp', e.target.value)}
                      placeholder="e.g., Our proprietary AI algorithm, 15 years of industry experience..."
                      className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      rows={2}
                    />
                  </div>
                  {/* Anecdote Input (Optional) */}
                  <div>
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 block`}>
                      Brand Story / Anecdote (Optional)
                    </label>
                    <textarea
                      value={customizations[topic.id]?.anecdote || ''}
                      onChange={(e) => handleCustomizationChange(topic.id, 'anecdote', e.target.value)}
                      placeholder="e.g., A success story related to this topic..."
                      className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCustomization(false)}
                disabled={isGenerating}
                className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                data-telemetry-manual="true"
                onClick={handleGenerateBlogs}
                disabled={isGenerating || isSubmittingGeneration}
                className={`px-6 py-3 rounded-lg font-medium transition-all shadow-lg whitespace-nowrap flex items-center gap-2 ${
                  isGenerating || isSubmittingGeneration
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                }`}
              >
                {isGenerating || isSubmittingGeneration ? (
                  <Loader2 className="animate-spin w-5 h-5" />
                ) : (
                  <i className="ri-magic-line"></i>
                )}
                {isSubmittingGeneration
                  ? 'Checking Credits...'
                  : isGenerating
                  ? 'Generating Blogs...'
                  : 'Generate Blogs'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingBlog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`w-full max-w-7xl h-[90vh] flex flex-col rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`shrink-0 p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
              <div>
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Edit Blog: {editingBlog.topic}
                </h3>
                <div className="flex mt-4 space-x-0 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setEditMode('write')}
                    className={`px-6 py-2 text-sm font-medium border-b-2 transition-colors ${editMode === 'write'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Write
                  </button>
                  <button
                    onClick={() => setEditMode('preview')}
                    className={`px-6 py-2 text-sm font-medium border-b-2 transition-colors ${editMode === 'preview'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              <button
                onClick={() => setEditingBlog(null)}
                className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 min-h-0 p-6 overflow-hidden flex flex-col">
              {editMode === 'write' ? (
                <BlogRichEditor
                  html={editContent}
                  onChange={setEditContent}
                  darkMode={!!darkMode}
                />
              ) : (
                <div className="flex-1 min-h-0 w-full rounded-xl overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
                  <div
                    className={`blog-rich-editor-canvas flex-1 min-h-0 w-full p-8 overflow-y-auto ${
                      darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: (editContent || '').replace(
                        /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>/gi,
                        (match, href, rest) => {
                          if (/target=/i.test(match)) return match;
                          return `<a href="${href}" target="_blank" rel="noopener noreferrer"${rest}>`;
                        }
                      ),
                    }}
                  />
                  <style>{`
                    .blog-rich-editor-canvas h1 {
                      font-size: 2rem !important;
                      font-weight: 700 !important;
                      line-height: 1.25 !important;
                      margin: 1em 0 0.5em !important;
                    }
                    .blog-rich-editor-canvas h2 {
                      font-size: 1.5rem !important;
                      font-weight: 700 !important;
                      line-height: 1.3 !important;
                      margin: 0.85em 0 0.4em !important;
                    }
                    .blog-rich-editor-canvas h3 {
                      font-size: 1.25rem !important;
                      font-weight: 600 !important;
                      line-height: 1.35 !important;
                      margin: 0.75em 0 0.35em !important;
                    }
                    .blog-rich-editor-canvas p {
                      margin: 0.75em 0 !important;
                      line-height: 1.7 !important;
                    }
                    .blog-rich-editor-canvas strong, .blog-rich-editor-canvas b {
                      font-weight: 700 !important;
                    }
                    .blog-rich-editor-canvas em, .blog-rich-editor-canvas i {
                      font-style: italic !important;
                    }
                    .blog-rich-editor-canvas u {
                      text-decoration: underline !important;
                    }
                    .blog-rich-editor-canvas ul {
                      list-style-type: disc !important;
                      padding-left: 2em !important;
                      margin: 0.5em 0 !important;
                    }
                    .blog-rich-editor-canvas ol {
                      list-style-type: decimal !important;
                      padding-left: 2em !important;
                      margin: 0.5em 0 !important;
                    }
                    .blog-rich-editor-canvas li {
                      display: list-item !important;
                      margin: 0.25em 0 !important;
                    }
                    .blog-rich-editor-canvas blockquote {
                      border-left: 4px solid #3b82f6 !important;
                      padding-left: 1em !important;
                      margin: 0.75em 0 !important;
                      font-style: italic !important;
                      color: ${darkMode ? '#d1d5db' : '#4b5563'} !important;
                    }
                    .blog-rich-editor-canvas a {
                      color: #2563eb !important;
                      text-decoration: underline !important;
                      font-weight: 500 !important;
                      cursor: pointer !important;
                    }
                    .blog-rich-editor-canvas a:hover {
                      color: #1d4ed8 !important;
                    }
                  `}</style>
                </div>
              )}
            </div>

            <div className={`shrink-0 p-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end space-x-3`}>
              <button
                onClick={() => setEditingBlog(null)}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${darkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                Cancel
              </button>
              <button
                data-telemetry-manual="true"
                onClick={handleSaveEdit}
                disabled={isSavingBlog}
                className={`px-6 py-2 rounded-lg font-medium transition-colors shadow-lg flex items-center gap-2 ${
                  isSavingBlog
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                }`}
              >
                {isSavingBlog ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Blogs Tab */}
      {activeTab === 'generated' && (
        <div className="space-y-4">
          {generatedBlogs.length === 0 ? (
            <div className={`p-12 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} text-center`}>
              <i className="ri-article-line text-5xl text-gray-400 mb-4"></i>
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                No Blogs Generated Yet
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                Start by selecting topics from the Topic Discovery tab
              </p>
              <button
                onClick={() => setActiveTab('discovery')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Discover Topics
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedBlogs.map((blog) => (
                <div key={blog.id} className={`p-5 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:shadow-lg transition-all`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${blog.published
                      ? 'bg-green-100 text-green-700'
                      : 'bg-orange-100 text-orange-700'
                      }`}>
                      {blog.published ? 'Published' : 'Draft'}
                    </div>
                  </div>

                  {/* Blog cover image + Regenerate button */}
                  <div className="relative w-full h-40 mb-3 rounded-md overflow-hidden group bg-gray-100 dark:bg-gray-700">
                    {(blog.image_url && !imgFallbacks[blog.id]) ? (
                      <>
                        <img
                          src={getFullImageUrl(blog.image_url)}
                          alt={blog.topic}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={() => handleImageError(blog.id)}
                        />
                        {/* Download button — top-right, revealed on hover */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(blog); }}
                          title="Download cover image"
                          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/70 cursor-pointer"
                        >
                          <i className="ri-download-2-line text-sm" />
                        </button>
                        {/* Regenerate button — bottom-right, revealed on hover */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRegenerateImage(blog); }}
                          disabled={regeneratingImageId === blog.id}
                          title="Regenerate poster"
                          className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/80 cursor-pointer disabled:opacity-60"
                        >
                          {regeneratingImageId === blog.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                            : <><i className="ri-image-edit-line" /> Regenerate</>}
                        </button>
                      </>
                    ) : (
                      /* No image or load error — show placeholder + Regenerate CTA */
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                        <i className="ri-image-2-line text-3xl text-gray-400 dark:text-gray-500" />
                        <button
                          onClick={() => handleRegenerateImage(blog)}
                          disabled={regeneratingImageId === blog.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow transition-all disabled:opacity-60 cursor-pointer"
                        >
                          {regeneratingImageId === blog.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                            : <><i className="ri-refresh-line" /> Regenerate Poster</>}
                        </button>
                      </div>
                    )}
                  </div>

                  <h4 className={`font-semibold mb-3 line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {blog.topic}
                  </h4>

                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">
                        {blog.author.charAt(0)}
                      </span>
                    </div>
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {blog.author}
                    </span>
                  </div>

                  <div className="flex items-center space-x-4 mb-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <i className="ri-file-text-line text-gray-500"></i>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                        {blog.wordCount} words
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* Publish / Unpublish Button */}
                    <button
                      data-telemetry-manual="true"
                      onClick={() => handlePublishToggle(blog)}
                      disabled={publishingBlogId === blog.id}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                        blog.published
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      } ${publishingBlogId === blog.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={blog.published ? 'Click to revert to Draft' : 'Mark as Published after posting to your website'}
                    >
                      {publishingBlogId === blog.id ? (
                        <><i className="ri-loader-4-line animate-spin" /> Updating...</>
                      ) : blog.published ? (
                        <><i className="ri-checkbox-circle-fill" /> Published ✓</>
                      ) : (
                        <><i className="ri-send-plane-line" /> Mark as Published</>
                      )}
                    </button>

                    {/* Edit / Download row */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(blog)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        Edit
                      </button>
                      <div className="flex items-center space-x-1 flex-1">
                        <button
                          data-telemetry-manual="true"
                          disabled={downloadingPdfBlogId === blog.id}
                          onClick={() => handleDownloadPDF(blog)}
                          className={`flex-1 px-2 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1 ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${downloadingPdfBlogId === blog.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title="Download PDF"
                        >
                          {downloadingPdfBlogId === blog.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                          <span>PDF</span>
                        </button>
                        <button
                          data-telemetry-manual="true"
                          disabled={downloadingDocxBlogId === blog.id}
                          onClick={() => handleDownloadDocx(blog)}
                          className={`flex-1 px-2 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1 ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${downloadingDocxBlogId === blog.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title="Download Word"
                        >
                          {downloadingDocxBlogId === blog.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileType className="w-4 h-4" />
                          )}
                          <span>Docx</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* One-time Informational Notice Modal on Generation Start */}
      {showStartNoticeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-fade-in"
          onClick={() => setShowStartNoticeModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700 relative animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowStartNoticeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>

            <div className="flex items-start space-x-3.5 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0 text-xl">
                <i className="ri-magic-line" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Blog Generation Started
                </h3>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                  Running in background
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
              Blog generation can take up to 10 minutes. You can continue working on other modules while it generates in the background. We'll notify you in the notification bell when it's ready.
            </p>

            <div className="flex justify-end">
              <button
                onClick={() => setShowStartNoticeModal(false)}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm cursor-pointer"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl max-w-md w-full border border-gray-100 dark:border-gray-700 animate-scale-up">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Cancel Blog Generation?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                  Are you sure you want to cancel generating this blog? Any used credits will be refunded to your balance, and your monthly free quota will not be reduced.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                disabled={isCancelling}
                onClick={() => setShowCancelConfirmModal(false)}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-all cursor-pointer disabled:opacity-50"
              >
                Continue Generating
              </button>
              <button
                type="button"
                disabled={isCancelling}
                onClick={handleConfirmCancelJob}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium text-sm transition-all shadow-md shadow-red-600/20 flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Cancelling...</span>
                  </>
                ) : (
                  <span>Yes, Cancel Generation</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <LowCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentCredits={modalCredits}
        requiredCredits={modalRequired}
        isBlocker={modalIsBlocker}
      />
      <CreditDeductionConfirmModal
        isOpen={showCreditConfirm}
        onClose={() => setShowCreditConfirm(false)}
        onConfirm={handleConfirmBlogGeneration}
        currentCredits={creditConfirmBalance}
        deductCredits={creditConfirmAmount}
        isProcessing={isConfirmingGeneration}
        description={
          creditConfirmDescription || 
          (quota && quota.limit > 0 
            ? `You've used your free monthly blog quota. Generating these will cost ${creditConfirmAmount.toLocaleString()} credits (${CREDIT_COSTS.SEO_BLOG} credits each), charged upfront.`
            : `Generating these will cost ${creditConfirmAmount.toLocaleString()} credits (${CREDIT_COSTS.SEO_BLOG} credits each), charged upfront.`)
        }
      />
    </div>
  );
}

